import { createEffect, createEvent, createStore } from 'effector';
import { deleteFileClientApi, getFilesClientApi, patchAttachmentClientApi } from '@nx-frontend/shared/api';
import {
  createInitialEntityState,
  EntityState,
  BucketFile,
  GetBucketFilesParams,
  mapBucketFilesDtoToBucketFiles,
  FileType,
} from '@nx-frontend/shared/types';
import { getPermission } from '@nx-frontend/shared/utils';

export type FileState = EntityState<BucketFile>;

export const updateAttachmentFx = createEffect(async (params: { globalId: string; filename: string }) => {
  await patchAttachmentClientApi(params);
  return { globalId: params.globalId, filename: params.filename };
});

export const fetchFilesFx = createEffect(async (params: GetBucketFilesParams) => {
  const result = await getFilesClientApi(params);
  return mapBucketFilesDtoToBucketFiles(result.data);
});

export const deleteFileFx = createEffect(async ({ globalId }: { globalId: string }) => {
  deleteFileEvent({ id: globalId });
  return await deleteFileClientApi(globalId);
});

export const deleteFileEvent = createEvent<{ id: string }>();
export const upsertFileEvent = createEvent<BucketFile>();
export const $filesState = createStore<FileState>(createInitialEntityState())
  .on(deleteFileEvent, (state, { id }) => {
    const entities = { ...state.entities };
    delete entities[id];
    return {
      entities,
      ids: Object.keys(entities),
    };
  })
  .on(updateAttachmentFx.doneData, (state, { globalId, filename }) => {
    const entities = { ...state.entities };
    const file = entities[globalId];
    if (file) {
      entities[globalId] = { ...file, filename };
    }
    return {
      entities,
      ids: Object.keys(entities),
    };
  })
  .on(fetchFilesFx.doneData, (state, files) => {
    const entities = { ...state.entities };
    files.forEach((file) => {
      entities[file.globalId] = file;
    });
    return { entities, ids: Object.keys(entities) };
  })
  .on(upsertFileEvent, (state, file) => {
    const entities = { ...state.entities };
    entities[file.globalId] = { ...file };
    return {
      ...state,
      entities,
      ids: Object.keys(entities),
    };
  });

export function filesToState(files: BucketFile[]): FileState {
  const entities: Record<string, BucketFile> = {};
  files.forEach((file) => {
    entities[file.globalId] = file;
  });
  return { entities, ids: Object.keys(entities) };
}

export function getSortedDashboardFileIds(allowedTargets?: string[]) {
  return {
    keys: [],
    store: $filesState,
    fn: (state: FileState) => {
      let sortedFiles = Object.values(state.entities).sort((a, b) => b.createdAt - a.createdAt);
      if (allowedTargets) {
        sortedFiles = sortedFiles.filter((file) => allowedTargets.includes(file.target));
      }
      return sortedFiles.map((file) => file.globalId);
    },
  };
}

export const getMockFilesForManager = () => {
  const filesToShow: BucketFile[] = [
    {
      globalId: 'fsdf7878',
      bucketId: '3242343fsdfsdf',
      target: 'fsdfds',
      targetId: 'fsdfsdfdsf',
      workspaceId: '35v6whxwcfuz20ui',
      filename: 'some file',
      type: FileType.FILE,
      userId: 312312,
      size: 45,
      createdAt: 423423423423432,
      deleted: false,
      url: 'fsdfsdfsdf',
    },
    {
      globalId: 'fsdf7878324324',
      bucketId: '3242343fsdfsdf',
      target: 'fsdfds',
      targetId: 'fsdfsdfdsf',
      workspaceId: '35v6whxwcfuz20ui',
      filename: 'some file',
      type: FileType.FILE,
      userId: 312312,
      size: 45,
      createdAt: 423423423423432,
      deleted: false,
      url: 'fsdfsdfsdf',
    },
  ];

  return filesToShow;
};

export function getFileByIdParams(id: string) {
  return {
    keys: [id],
    store: $filesState,
    fn: (state: FileState, [keyId]: string[]) => state.entities[keyId],
    updateFilter: (prev: BucketFile | undefined, next: BucketFile | undefined) => {
      return prev?.filename !== next?.filename;
    },
  };
}

export function getRecentFileIdsParams(
  numberOfItems: number,
  allowedTargets?: string[],
  targetPageIds?: string[],
  personalizationUserId?: number | null
) {
  return {
    keys: [numberOfItems, personalizationUserId],
    store: $filesState,
    defaultValue: [],
    fn: (state: FileState, [count, personalizationUserId]: (number | undefined | null)[]): string[] => {
      let sortedFiles = Object.values(state.entities).sort((a, b) => b.createdAt - a.createdAt);
      if (allowedTargets) {
        sortedFiles = sortedFiles.filter(
          (file) =>
            allowedTargets.includes(file.target) &&
            (!targetPageIds || (file.pageId && targetPageIds.includes(file.pageId)))
        );
      }

      if (personalizationUserId) {
        sortedFiles = sortedFiles.filter(
          (file) =>
            !file.permissions ||
            (file.permissions && getPermission({ userId: personalizationUserId, permissions: file.permissions }))
        );
      }

      return sortedFiles.slice(0, count ?? 5).map((file) => file.globalId);
    },
  };
}

export function getFilesByTargetId(targetId: string) {
  return {
    keys: [targetId],
    store: $filesState,
    defaultValue: [],
    fn: (state: FileState, [targetId]: string[]): BucketFile[] =>
      Object.values(state.entities)
        .filter((file) => file.targetId === targetId)
        .sort((a, b) => b.createdAt - a.createdAt),
  };
}
